import React from 'react';
import { Link } from 'react-router-dom';
import CopyButton from '../Common/CopyButton';

const CreateAgentPage = () => {
  const agentData = {
    name: 'Course Discovery Helper',
    description: 'Helps students explore courses by interest, requirement, credits & level. Demo data—confirm with your institution.',
    instructions: `You are a course discovery assistant using the uploaded Sample Catalog dataset (demo).
When a student is unsure what to take, ask 2–3 quick questions: interests, requirement needs (writing, science, etc.), desired credit load, and whether they want beginner-friendly classes.
Match queries against these fields in the data: title, desc_short, interest_tags, gened_tags, credits_min/max, difficulty, prereq_flag, experience_tags, modality.
Return short suggestion lists (3–6 items). Use headings when helpful:
• Starter Picks (intro, low prereqs)
• Fulfills Requirement (matches gen-ed tag requested)
• Try Something Different (outside stated interest)
Each result: course_id – title (credits) + short summary. Mention difficulty or prereq note if flagged.
If the student asks for official degree applicability, current availability, or enrollment info: say this is demo data and advise checking the institution's catalog or an academic advisor.
If the student asks for assignment, quiz, or lecture help: direct them to their CampusMind course assistant (not you).
Always append: Demo data—confirm with your institution.
Do not invent courses not present in the uploaded data.`,
    conversationStarters: [
      'I don\'t know what to take—help me choose.',
      'I need a science class.',
      'Show beginner data or coding courses.',
      'I still need a writing requirement.',
      'Something outside my major?'
    ]
  };

  return (
    <div className="min-h-screen bg-purple-50">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-2xl md:text-3xl font-bold text-purple-700 mb-4">
            Create a Course Discovery Agent to Boost Readiness and Reduce Workload
          </h1>

          <p className="text-lg text-gray-800 mb-4">
            Help students explore the courses available to them, understand their options, and choose the right path with confidence and ease.
          </p>

          <div className="mb-8">
            <a 
              href="https://app.campusmind.ai/agents-market" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-800 transition-all duration-200 shadow-lg hover:shadow-xl text-lg"
            >
              Try AI Agents Now →
            </a>
          </div>
        </div>

        {/* Navigation video */}
        <div className="flex justify-center mb-12">
          <div className="w-full max-w-3xl bg-white rounded-2xl shadow-md p-6 md:p-8">
            <div className="text-xl md:text-2xl font-bold mb-2">Navigate</div>
            <div className="text-purple-700 font-semibold text-lg mb-6 text-center">
              Back → Agent Marketplace → My Agents → Create Agent
            </div>
            <img
              src="/genericagent.gif"
              alt="Generic Agent Demo"
              className="w-full rounded-xl shadow-lg"
            />
          </div>
        </div>

        {/* Agent creation form */}
        <div className="flex justify-center mb-16">
          <form className="bg-white rounded-2xl shadow-md p-6 md:p-10 w-full max-w-3xl">
            <div className="mb-8">
              <div className="text-2xl md:text-3xl font-bold mb-4">
                Create an Agentic
              </div>

              <div className="text-gray-600 text-lg leading-relaxed mb-6">
                <div className="mb-4">
                  <strong>Step 1:</strong> Copy all the fields provided below, one at a time.
                </div>

                <div className="mb-4">
                  <strong>Step 2:</strong> Paste each field into its respective place in the agent creation form.
                  <div className="text-sm text-gray-500 ml-4 mt-1">
                    This ensures your agent has the correct name, purpose, behavior, and prompts.
                  </div>
                </div>

                <div className="mb-4">
                  <strong>Step 3:</strong> Upload the data files the agent will use. (available below)
                </div>

                <div>
                  <strong>Step 4:</strong> Review everything and click Save to finish setting up your agent.
                </div>
              </div>

              {/* Name field */}
              <div className="mb-6">
                <label className="block font-semibold mb-2">Name this Agent</label>
                <div className="flex items-center flex-wrap gap-2">
                  <input
                    type="text"
                    readOnly
                    value={agentData.name}
                    className="flex-1 min-w-48 px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                  />
                  <CopyButton value={agentData.name} />
                </div>
              </div>

              {/* Description field */}
              <div className="mb-6">
                <label className="block font-semibold mb-2">Description</label>
                <div className="flex items-center flex-wrap gap-2">
                  <input
                    type="text"
                    readOnly
                    value={agentData.description}
                    className="flex-1 min-w-48 px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                  />
                  <CopyButton value={agentData.description} />
                </div>
              </div>

              {/* Instructions field */}
              <div className="mb-6">
                <label className="block font-semibold mb-2">Instructions</label>
                <div className="flex items-start flex-wrap gap-2">
                  <textarea
                    readOnly
                    value={agentData.instructions}
                    className="flex-1 min-w-48 min-h-32 px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 resize-vertical"
                  />
                  <CopyButton value={agentData.instructions} />
                </div>
              </div>

              {/* Conversation starters */}
              <div className="mb-6">
                <label className="block font-semibold mb-2">Conversation starters</label>
                <div className="space-y-4">
                  {agentData.conversationStarters.map((starter, index) => (
                    <div key={index} className="flex items-center flex-wrap gap-2">
                      <input
                        type="text"
                        readOnly
                        value={starter}
                        className="flex-1 min-w-48 px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                      />
                      <CopyButton value={starter} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Category field */}
              <div className="mb-6">
                <label className="block font-semibold mb-2">Category</label>
                <select
                  disabled
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                >
                  <option>academic affirs</option>
                </select>
              </div>

              {/* Knowledge Base section */}
              <div className="mt-8">
                <label className="block font-semibold mb-2">Knowledge Base</label>
                <div className="text-gray-600 mb-3">
                  Download and drag this file into the Knowledge Base section in the Lab.
                </div>
                <a
                  href="/sample_catalog_discovery.csv"
                  download
                  className="block w-full p-8 border-2 border-dashed border-purple-300 rounded-xl bg-purple-50 text-center text-purple-700 font-semibold text-lg no-underline hover:bg-purple-100 transition-colors duration-200"
                >
                  📄 sample_catalog_discovery.csv
                  <br />
                  <span className="font-normal text-gray-600 text-base">
                    (Drag and drop this file into the Lab)
                  </span>
                </a>
              </div>
            </div>
          </form>
        </div>

        <div className="text-center">
          <div className="text-lg text-blue-600 font-bold mb-4">
            <strong>Done!</strong><br />
            You've just created an agent designed to simplify the support experience and reduce confusion for everyone it serves.
          </div>

          <div className="text-base text-gray-700 font-medium mb-8">
            <strong>Next up:</strong> You're ready to explore the diverse range of agents in the marketplace and see what else is possible.
          </div>

          <div className="flex justify-center gap-4 md:gap-8 flex-wrap">
            <Link
              to="/test"
              className="bg-gray-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-700 transition-colors duration-200"
            >
              Back: Test CampusMind
            </Link>
            <Link
              to="/accessibility-agent"
              className="bg-purple-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-800 transition-colors duration-200"
            >
              Next: Accessibility Agent
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAgentPage; 